'use client';
import cn from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '../Input/Input';

import { AutoCompleteProps } from './AutoComplete.types';

import styles from './Styles/Default.module.css';
import Option from './Components/Option';
import RadioOption from './Components/RadioOption';
import { AutoCompleteOptionProps } from './Components/AutocompleteOption.types';
import { CancelCircle } from '../Icons/Paths/CancelCircle';
import { Search } from '../Icons/Paths/Search';
import { slugify } from './Utils/Slugify';

export const AutoComplete: FC<AutoCompleteProps> = ({
  Component = 'div',
  value,
  onChange,
  options,
  disabled = false,
  dataTestId,
  onBlur,
  onClick,
  label,
  hint,
  inputRef,
  placeholder,
  error,
  inputChange,
  className,
  rightIcon,
  autoComplete = true,
  onReset,
  ...restProps
}) => {
  const [stateValue, setStateValue] = useState<AutoCompleteOptionProps | undefined>(
    value ? { value: value, label: value } : undefined,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedOptionRef = useRef<HTMLLIElement>(null);

  const filterSuggestions = options?.filter(i =>
    slugify(i.label.toLowerCase()).includes(value ? slugify(value?.toLowerCase()) : ''),
  );

  const selectOption = useCallback(
    (option: AutoCompleteOptionProps) => {
      if (onChange) {
        if (option !== stateValue) onChange(option);
      }

      setStateValue(option);
    },
    [onChange, stateValue],
  );

  const onClickHandler = useCallback(() => {
    onClick?.();
    if (!disabled) setIsOpen(nextState => !nextState);
  }, [onClick, disabled, setIsOpen]);

  useEffect(() => {
    if (isOpen) setHighlightedIndex(0);
  }, [isOpen]);

  const onBlurHandler = useCallback(() => {
    onBlur?.();
    setIsOpen(false);
  }, [onBlur, setIsOpen]);

  const classNames = cn(
    styles.container,
    isOpen && styles.isOpen,
    disabled && styles.disabled,
    className,
  );

  const valueClassNames = cn(disabled && 'disabled', styles.value);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputChange) {
      inputChange(e);
    }

    if (filterSuggestions.length > 0) {
      setIsOpen(true);
      setHighlightedIndex(0);
    } else {
      setIsOpen(false);
    }
  };
  const allOptions = autoComplete ? filterSuggestions : options;

  const isOptionSelection = (option: AutoCompleteOptionProps) => {
    return option?.value === stateValue?.value;
  };
  const handleOnKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    e => {
      switch (e.code) {
        case 'Enter':
          if (!isOpen) {
            setIsOpen(true);
          } else if (allOptions.length > 0) {
            selectOption(allOptions[highlightedIndex]);
            setIsOpen(false);
          }
          break;
        case 'ArrowUp': {
          e.preventDefault();
          const newValueUp = highlightedIndex === 0 ? allOptions.length - 1 : highlightedIndex - 1;
          setHighlightedIndex(newValueUp);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const nextValueDown = (highlightedIndex + 1) % allOptions.length;
          setHighlightedIndex(nextValueDown);
          break;
        }
      }

      highlightedOptionRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      });
    },
    [highlightedIndex, options, selectOption],
  );
  const ClearButton = (
    <CancelCircle
      className={cn(disabled && styles.disabled, styles.clearIcon)}
      {...(!disabled && { onClick: onReset })}
    />
  );

  return (
    <Component className={classNames} onBlur={onBlurHandler} ref={containerRef} tabIndex={0}>
      <Input
        aria-disabled={disabled}
        onMouseDown={onClickHandler}
        className={valueClassNames}
        disabled={disabled}
        error={error}
        fullWidth={true}
        hint={hint}
        label={label}
        onChange={onInputChange}
        onKeyDown={handleOnKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        leftIcon={!value && <Search />}
        {...((rightIcon || onReset) && {
          rightIcon: onReset && value ? ClearButton : rightIcon,
        })}
        value={value}
        {...restProps}
      />

      <ul className={`${styles.options} ${isOpen ? styles.show : ''}`} data-test-id={dataTestId}>
        {allOptions.map((option, index) => (
          <li
            ref={highlightedIndex === index ? highlightedOptionRef : null}
            className={`${styles.option}   ${index === highlightedIndex ? styles.highlighted : ''}`}
            key={`${option.value}+${index}`}
            onMouseDown={e => {
              selectOption(option);
              e.stopPropagation();
              setIsOpen(false);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <Option data-desktop-only {...option} />
            <RadioOption data-mobile-only checked={isOptionSelection(option)} {...option} />
          </li>
        ))}
      </ul>
    </Component>
  );
};

AutoComplete.displayName = 'AutoComplete';
export type { AutoCompleteOptionProps };
